import React, { useEffect, useState } from "react";
import { Box, Typography, Button, Paper, Container, ThemeProvider, CircularProgress } from "@material-ui/core";
import { createMuiTheme } from "@material-ui/core/styles";
import Rating from "@material-ui/lab/Rating";
import StarIcon from "@material-ui/icons/Star";
import { withStyles } from "@material-ui/core/styles";
import { TopBar } from "../../../TopBar/TopBar";
import { NuvvenDivider } from "../../Summary/Components/Divider";
import { useHistory, useParams } from "react-router-dom";
import { ApolloError, useLazyQuery, useMutation } from "@apollo/client";
import { GET_JOB } from "../../../../graphql/task/getTaskById";
import { formatGraphQLErrorMessage } from "../../../utils";
import { SnackBarVariant } from "../../../SnackbarWrapper/SnackbarWrapper";
import { useSnackBar } from "../../../SnackBarContext/SnackBarContext";
import { UPDATE_DRIVER_RATING } from "../../../../graphql/task/updateDriverRating";
import { useTranslation } from "react-i18next";
export interface IUserJob {
  id: string
  subCategory: string
  status: string
  jobDate: string
  dateCreated: string
  description: string
  assignedTo?: string
  booking?: {
    id: string;
    referenceNumber: string;
  }
  rating?: IRating;
  lastKnownDriverLocation:{
    id: string;
    latitude: number;
    longitude: number;
    timestamp: string;
  };
  address:any;
}

// Data model for rating
interface IRating {
  knowledge: number;
  behavior: number;
  punctuality: number;
}

// Custom styled Rating component (since `MuiRating` is not available in `overrides`)
const StyledRating = withStyles({
  iconFilled: { color: "#ff5722" }, // Orange/red color for filled stars
  iconEmpty: { color: "#e0e0e0" }, // Light gray empty stars
})(Rating);

// Theme configuration for Material-UI v4
const theme = createMuiTheme({
  palette: {
    primary: { main: "#7b1fa2" }, // Purple color
    secondary: { main: "#ffeb3b" }, // Yellow for submit button
  },
});

const categories = ["Knowledge", "Behavior", "Punctuality"]

const DriverRating: React.FC = () => {
  const history = useHistory();
  const snackbar = useSnackBar();
  const { jobId } = useParams<{ jobId: string }>();
  const [userJob, setUserJob] = useState<IUserJob>();
  const [rating, setRating] = useState<IRating>({
    knowledge: 0,
    behavior: 0,
    punctuality: 0
  });
  const {t} = useTranslation();

  const [fetchJob, { loading: jobDataLoading }] = useLazyQuery(GET_JOB, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data.getTaskById) {
        setUserJob(data.getTaskById)
      }
    },
    onError: (error: ApolloError) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      });
    }
  });

  const [updateDriverRating] = useMutation(UPDATE_DRIVER_RATING, {
    onCompleted: (data) => {
      if (data) {
        snackbar({
          message: t("snackBar:feedbackSaved"),
          variant: SnackBarVariant.SUCCESS
        });
        history.push("/");
      }
    },
    onError: (error: ApolloError) => {
      snackbar({
        message: formatGraphQLErrorMessage(error.message),
        variant: SnackBarVariant.ERROR
      });
    }
  });

  useEffect(() => {
    if (jobId) {
      fetchJob({
        variables: {
          id: jobId,
        },
      });
    }
  }, [jobId]);

  const handleRatingChange = (category: keyof IRating, value: number | null) => {
    setRating((prev) => ({ ...prev, [category]: value || 0 }));
  };

  const handleSubmit = () => {
    if (userJob) {
      const { subCategory, status } = userJob;
      updateDriverRating({
        variables: {
          id: jobId,
          userJobData: {
            subCategory,
            status,
            rating
          }
        }
      })
    }
  };

  return (
    <>
      <TopBar height={70} logoSize={44} />
      <ThemeProvider theme={theme}>
        <Container maxWidth="xs" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
          <Paper elevation={3} style={{ overflow: "hidden", borderRadius: 8, width: "100%", padding: 25 }}>
            {jobDataLoading ? (
              <Box
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%"
                }}
              >
                <CircularProgress size={24} />
              </Box>) : !userJob ? (
                <Typography style={{ padding: 16, textAlign: "center" }}>
                  {t("root:taskDetailsNotFound")}
                </Typography>
              ) : (
              <>
                <Box style={{ padding: "10px 0", textAlign: "center" }}>
                  <Typography variant="h5" style={{ fontWeight: "bold" }}>{t("root:bookingRef")}: {userJob.booking?.referenceNumber}</Typography>
                </Box>
                <NuvvenDivider noMargin={true} />

                <Typography variant="h6" style={{ padding: "10px 0" }}>{t("root:rateYourExperience")}</Typography>
                {categories.map((category) => (
                  <Box key={category} style={{ marginBottom: 24 }}>
                    <Typography variant="subtitle1" style={{ fontWeight: "bold", marginBottom: 8 }}>
                      {t<string>(`misc:categoriesKey.${category}`)}:
                    </Typography>
                    <StyledRating
                      name={category}
                      value={rating[category as keyof IRating]}
                      onChange={(_, value) => handleRatingChange(category.toLowerCase() as keyof IRating, value)}
                      precision={1}
                      icon={<StarIcon fontSize="large" />}
                      emptyIcon={<StarIcon fontSize="large" />}
                      style={{ fontSize: 32 }}
                    />
                  </Box>
                ))}

                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSubmit}
                  style={{
                    backgroundColor: theme.palette.secondary.main,
                    color: "black",
                    padding: "12px",
                    fontWeight: "bold",
                    marginTop: 12,
                  }}
                >
                  {t("root:submit").toUpperCase()}
                </Button>
              </>
            )}
          </Paper>
        </Container>
      </ThemeProvider>
    </>
  );
};

export default DriverRating;